import { Box, Flex, Spinner, Text } from "@chakra-ui/react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { t } from "i18next";
import { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useAccountData } from "../../../hooks/useAccountData";
import { useIsVisible } from "../../../hooks/useIsVisible";
import { useUserData } from "../../../hooks/useUserData";
import { EventTypes } from "../../../screens/root/api/eventsApi";
import { useMobileBreakpoint } from "../../../utils/useBreakpoints";
import { AuthFunnel } from "../../auth/components/AuthFunnel";
import { FUNNEL_TYPES } from "../../funnel/utils/contants";
import { FunnelComponentProps } from "../../funnel/types";
import {
  GET_GIFTCARD_PRICING_QUERY_KEY,
  GiftCardPaymentSetupPayload,
  createGiftCardPurchaseFromConfirmationToken,
  createGiftCardPurchaseFromSavedPaymentMethod,
  getGiftCardPricing,
  isGiftCardPaymentSetupPayload,
} from "../api/paymentApi";
import { useIsShowingEmbeddedStripeForm } from "../hooks/useIsShowingEmbeddedStripeForm";
import { usePaymentCompletion } from "../hooks/usePaymentCompletion";
import { usePendingAuthPurchaseResume } from "../hooks/usePendingAuthPurchaseResume";
import { SubscriptionPriceTypes } from "../types";
import {
  clearGuestPendingPurchase,
  saveGuestPendingPurchase,
} from "../utils/guestPendingPurchase";
import { createSessionPayloadStore } from "../utils/sessionPayloadStore";
import { EmbeddedStripePaymentForm } from "./EmbeddedStripePaymentForm";
import { PaymentEyebrow } from "./PaymentEyebrow";
import { PaymentTitle } from "./PaymentTitle";
import { PaymentValueProps } from "./PaymentValueProps";
import { PromoCodeInput } from "./PromoCodeInput";
import { PurchaseButton } from "./PurchaseButton";
import { SubscriptionSelector } from "./SubscriptionSelector";

const giftCardPayloadStore =
  createSessionPayloadStore<GiftCardPaymentSetupPayload>(
    "pendingGiftCardPaymentSetup",
    isGiftCardPaymentSetupPayload,
  );

export function PurchaseGiftCardModalBody({
  onClose,
  onSuccess: onSuccessProp,
  sendEvent,
  setFunnelType,
  updateFunnelConfig,
  hideValueProps = false,
  hidePromoCode = false,
  showEmailPasswordForm = false,
  centerWelcomeText = false,
  largeLoginCTA = false,
  initialPromoCode,
  customSubmitButtonText,
  showYearlyOnly = false,
}: FunnelComponentProps & {
  customSubmitButtonText?: string;
  hideValueProps?: boolean;
  hidePromoCode?: boolean;
  centerWelcomeText?: boolean;
  showEmailPasswordForm?: boolean;
  largeLoginCTA?: boolean;
  initialPromoCode?: string;
  showYearlyOnly?: boolean;
}) {
  const accountData = useAccountData();
  const { userData } = useUserData();
  const queryClient = useQueryClient();
  const [urlSearchParams] = useSearchParams();
  const showEmbeddedForm = useIsShowingEmbeddedStripeForm();
  const isMobile = useMobileBreakpoint();

  const [promoCode, setPromoCode] = useState<string>(
    initialPromoCode ?? urlSearchParams.get("code") ?? "",
  );
  const [appliedPromoCode, setAppliedPromoCode] = useState<string>(
    initialPromoCode ?? urlSearchParams.get("code") ?? "",
  );
  const [yearlySub, setYearlySub] = useState<SubscriptionPriceTypes>();
  const [monthlySub, setMonthlySub] = useState<SubscriptionPriceTypes>();
  const [activeType, setActiveType] = useState<"yearly" | "monthly">();
  const [activeAuthType, setActiveAuthType] = useState<FUNNEL_TYPES>();

  const queryKey = useMemo(
    () => [...GET_GIFTCARD_PRICING_QUERY_KEY, appliedPromoCode],
    [appliedPromoCode],
  );
  const { data } = useQuery({
    enabled: !!accountData,
    queryKey,
    queryFn: () => getGiftCardPricing(appliedPromoCode),
    refetchOnWindowFocus: false,
  });

  useEffect(() => {
    const subscriptions = data?.data.payload.results?.subscriptions;
    if (!subscriptions) return;
    setYearlySub(subscriptions.find((s) => s.billing_frequency === "yearly"));
    setMonthlySub(subscriptions.find((s) => s.billing_frequency === "monthly"));
  }, [data]);

  useEffect(() => {
    if (!activeType || !appliedPromoCode) {
      if (yearlySub?.promo_code) {
        setActiveType("yearly");
      } else if (monthlySub?.promo_code) {
        setActiveType("monthly");
      } else {
        setActiveType("yearly");
      }
    } else if (appliedPromoCode === yearlySub?.promo_code) {
      setActiveType("yearly");
    } else if (appliedPromoCode === monthlySub?.promo_code) {
      setActiveType("monthly");
    }
  }, [yearlySub, monthlySub]);

  const [isPromoValid, setIsPromoValid] = useState(true);
  const [promoValidationError, setPromoValidationError] = useState<string>();
  useEffect(() => {
    if ((!promoCode && !appliedPromoCode) || appliedPromoCode === promoCode) {
      setIsPromoValid(true);
      setPromoValidationError(undefined);
      return;
    }
    const timeout = setTimeout(() => {
      setIsPromoValid(false);
      setPromoValidationError(
        t("components.payments.subscription.applyPromoToComplete"),
      );
    }, 400);
    return () => clearTimeout(timeout);
  }, [promoCode, appliedPromoCode]);

  const activeSub = activeType === "yearly" ? yearlySub : monthlySub;
  const accessTokenTypeId = activeSub?.access_token_type_id ?? 0;
  const effectivePromoCode = promoCode.length ? promoCode : activeSub?.promo_code;

  const buildGiftCardSetupPayload = (
    confirmationTokenId: string,
  ): GiftCardPaymentSetupPayload => ({
    accessTokenTypeId,
    incentiveCode: effectivePromoCode ?? null,
    checkoutFlow: "gift_card_payment_setup",
    confirmationTokenId,
  });

  const finishSuccess = () => {
    onSuccessProp?.({
      data: [null, null],
      options: { didPurchaseSubscription: true },
    });
    onClose?.();
  };

  const invalidateGiftPricing = () =>
    queryClient.invalidateQueries({ queryKey: GET_GIFTCARD_PRICING_QUERY_KEY });

  const {
    run: runGiftCardPurchase,
    runAndSwallow: runGiftCardPurchaseAndSwallow,
    isLoading: isCompletingPurchase,
    error: checkoutError,
  } = usePaymentCompletion<
    Awaited<
      ReturnType<
        | typeof createGiftCardPurchaseFromConfirmationToken
        | typeof createGiftCardPurchaseFromSavedPaymentMethod
      >
    >
  >({
    updateCache: invalidateGiftPricing,
    onSuccess: () => {
      clearGuestPendingPurchase(giftCardPayloadStore);
      finishSuccess();
    },
    fallbackMessage: t(
      "components.payments.subscription.subscriptionDeclined",
    ) as string,
  });

  usePendingAuthPurchaseResume<GiftCardPaymentSetupPayload>({
    enabled: !!userData && accessTokenTypeId > 0,
    entityKey: accessTokenTypeId,
    payloadStore: giftCardPayloadStore,
    payloadMatchesEntity: (payload) =>
      payload.accessTokenTypeId === accessTokenTypeId,
    onResume: (payload) =>
      runGiftCardPurchase(() =>
        createGiftCardPurchaseFromConfirmationToken(payload),
      ),
  });

  const hasSavedPaymentMethod =
    data?.data?.payload?.results?.provider?.has_saved_payment_method === true;

  const handlePaymentSetupComplete = (confirmationTokenId: string) => {
    if (accessTokenTypeId <= 0) return;
    const payload = buildGiftCardSetupPayload(confirmationTokenId);

    if (!userData) {
      saveGuestPendingPurchase(payload, giftCardPayloadStore);
      setFunnelType(FUNNEL_TYPES.signup);
      return;
    }

    runGiftCardPurchaseAndSwallow(() =>
      createGiftCardPurchaseFromConfirmationToken(payload),
    );
  };

  const handleSavedPaymentMethodPurchase = () => {
    if (accessTokenTypeId <= 0) return;
    runGiftCardPurchaseAndSwallow(() =>
      createGiftCardPurchaseFromSavedPaymentMethod({
        accessTokenTypeId,
        incentiveCode: effectivePromoCode ?? null,
      }),
    );
  };

  const ref = useRef<HTMLDivElement>(null);
  const isVisible = useIsVisible(ref, 0.5);
  const alreadyTracked = useRef(false);
  useEffect(() => {
    if (sendEvent && isVisible && !alreadyTracked.current) {
      sendEvent(EventTypes.viewed_subscription_offer);
      alreadyTracked.current = true;
    }
  }, [isVisible]);

  if (!data || !yearlySub || !monthlySub) {
    return (
      <Flex
        width="100%"
        justifyContent="center"
        alignItems="center"
        height="531px"
        padding={isMobile ? "16px" : "24px"}
        ref={ref}
      >
        <Spinner color="blaze.blaze" />
      </Flex>
    );
  }

  const activePaywall =
    (activeType === yearlySub.billing_frequency
      ? yearlySub.paywalls
      : monthlySub.paywalls
    )?.find((paywall) => paywall.context === "modal");

  const submitButtonText =
    customSubmitButtonText ||
    (userData
      ? t("components.payments.subscription.confirmGiftCardPayment")
      : t("components.payments.subscription.subscribeToGlobalComixGold"));

  const responseError = checkoutError ?? promoValidationError;

  return (
    <Flex
      flexDirection="column"
      gap="24px"
      alignItems="center"
      borderRadius="16px"
      padding={isMobile ? "16px" : "24px"}
      ref={ref}
    >
      {activeAuthType ? null : (
        <Flex
          flexDirection="column"
          width="fit-content"
          gap="24px"
          alignItems="center"
        >
          {activePaywall?.eyebrow && (
            <PaymentEyebrow text={activePaywall.eyebrow} />
          )}
          <PaymentTitle textAlign="center" text={activePaywall?.main_title ?? ""} />
          {!hideValueProps && (
            <PaymentValueProps
              showSeparators={false}
              alignItems="start"
              valueProps={activePaywall?.value_props ?? ["", "", ""]}
            />
          )}
        </Flex>
      )}

      <Flex
        flexDirection="column"
        maxWidth="100%"
        width="100%"
        gap={isMobile ? "16px" : "24px"}
        alignItems="center"
      >
        {!activeAuthType ? (
          <>
            <Flex
              flexDirection="column"
              width="100%"
              gap="16px"
              paddingTop="14px"
            >
              <SubscriptionSelector
                onClick={setActiveType}
                subscriptionPrice={yearlySub}
                active={yearlySub.billing_frequency === activeType}
              />
              {!showYearlyOnly && (
                <SubscriptionSelector
                  onClick={setActiveType}
                  subscriptionPrice={monthlySub}
                  active={monthlySub.billing_frequency === activeType}
                />
              )}
              <Text
                color="neutral.300"
                fontSize="12px"
                lineHeight="150%"
                textAlign="center"
              >
                {t("components.payments.subscription.giftDelivery")}
              </Text>
            </Flex>
            {hidePromoCode ? null : (
              <PromoCodeInput
                promoApplied={appliedPromoCode}
                isValid={
                  appliedPromoCode === yearlySub.promo_code ||
                  appliedPromoCode === monthlySub.promo_code
                }
                applyPromoCode={setAppliedPromoCode}
                onChange={setPromoCode}
              />
            )}
            <Box width="100%">
              {isCompletingPurchase ? (
                <PurchaseButton
                  text="Confirming your purchase…"
                  loading
                  isDisabled
                />
              ) : hasSavedPaymentMethod || !showEmbeddedForm ? (
                <PurchaseButton
                  text={submitButtonText}
                  onClick={handleSavedPaymentMethodPurchase}
                  loading={isCompletingPurchase}
                  isDisabled={
                    isCompletingPurchase ||
                    !isPromoValid ||
                    accessTokenTypeId <= 0
                  }
                />
              ) : (
                <EmbeddedStripePaymentForm
                  submitButtonText={submitButtonText}
                  disabled={
                    isCompletingPurchase ||
                    !isPromoValid ||
                    accessTokenTypeId <= 0
                  }
                  onComplete={handlePaymentSetupComplete}
                />
              )}
            </Box>
            {responseError ? (
              <Text
                color="error.300"
                fontSize="14px"
                lineHeight="130%"
                width="100%"
              >
                {responseError}
              </Text>
            ) : null}
          </>
        ) : (
          <AuthFunnel
            activeAuthType={activeAuthType}
            setFunnelType={setActiveAuthType}
            updateFunnelConfig={updateFunnelConfig}
            onClose={onClose}
            onSuccess={() => undefined}
            waiting={isCompletingPurchase}
            sendEvent={sendEvent}
            centerWelcomeText={centerWelcomeText}
            showEmailPasswordForm={showEmailPasswordForm}
            largeLoginCTA={largeLoginCTA}
          />
        )}
      </Flex>
    </Flex>
  );
}
